import { STATUS_ENUM } from 'constants/report';

import React, { useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, Modal, Tooltip, useTheme } from '@mui/material';
import MaterialButton from 'components/MaterialButton';
import { hasCustomClassDbProducts, isSuccessful } from 'utils/reportUtils';

import CustomClassDbModal from './CustomClassDbModal';
import ApproveMapOnlyMenuItem from './items/ApproveMapOnlyMenuItem';
import ApproveReportMenuItem from './items/ApproveReportMenuItem';
import CloneReportMenuItem from './items/CloneReportMenuItem';
import CustomClassDbReportMenuItem from './items/CustomClassDbReportMenuItem';
import DisapproveReportMenuItem from './items/DisapproveReportMenuItem';
import RejectReportMenuItem from './items/RejectReportMenuItem';
import UnRejectReportMenuItem from './items/UnRejectReportMenuItem';
import UpdateReportMenuItem from './items/UpdateReportMenuItem';
import ViewReportMenuItem from './items/ViewReportMenuItem';
import useStyles from './useStyles';
import i18n from '../../i18n';

interface Props {
  updateReportJobStatus: (
    id: number,
    status: boolean,
    mapOnly: boolean,
    currentData: any[],
  ) => Promise<void>;
  res: reports.AllReportsQueryRow[];
  localData: any;
  setShowApprovalModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowMapOnlyApprovalModal: React.Dispatch<React.SetStateAction<boolean>>;
  setReportId: React.Dispatch<React.SetStateAction<string>>;
  setReportRowId: React.Dispatch<React.SetStateAction<number>>;
}

const ReportViewerMenu: React.FC<Props> = ({
  updateReportJobStatus,
  res,
  localData,
  setShowApprovalModal,
  setShowMapOnlyApprovalModal,
  setReportId,
  setReportRowId,
  // eslint-disable-next-line complexity -- pre-existing complexity (27); surfaced by the ESLint 9 flat-config migration (AIPD-1397), refactor tracked separately
}) => {
  const classes = useStyles(useTheme());
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [showCustomClassDbModal, setShowCustomClassDbModal] =
    useState<boolean>(false);
  const [showUpdateReportDisabledModal, setShowUpdateReportDisabledModal] =
    useState<boolean>(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Check if this is a Mix and Match report by checking params
  const isMixAndMatch = res[0].params && 
    (res[0].params as any).mix_and_match !== undefined;
  
  const isMenuDisabled =
    (res[0].reportType === 'update_products' || isMixAndMatch) &&
    res[0].reportStatus !== STATUS_ENUM.SUCCESS;

  return (
    <div>
      <Tooltip
        title={
          isMenuDisabled
            ? isMixAndMatch
              ? 'Options are available for Mix and Match in success status.'
              : 'Options are only available for Update Products reports in the Success status.'
            : ''
        }
        placement="top"
      >
        <span>
          <IconButton
            aria-label="more"
            id="long-button"
            aria-controls={open ? 'long-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleClick}
            disabled={isMenuDisabled}
          >
            <MoreVertIcon />
            {res[0].passedQa === true && (
              <span className={classes.text}>{i18n.t('reports.qaApproved')}</span>
            )}
            {res[0].passedQa === false && (
              <span className={classes.text}>{i18n.t('reports.qaRejected')}</span>
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {/* Allow cloning except for update_products and mix_and_match reports */}
        {res[0].reportType !== 'update_products' && !isMixAndMatch && (
          <CloneReportMenuItem handleClose={handleClose} report={res[0]} />
        )}

        {
          /* If successful and not reviewed, allow viewing */
          isSuccessful(res[0]) && res[0].passedQa === null && (
            <ViewReportMenuItem handleClose={handleClose} report={res[0]} />
          )
        }

        {
          /* If successful and not reviewed, allow approving */
          isSuccessful(res[0]) && res[0].passedQa === null && (
            <ApproveReportMenuItem
              handleClose={handleClose}
              report={res[0]}
              setShowApprovalModal={setShowApprovalModal}
              setReportId={setReportId}
              setReportRowId={setReportRowId}
            />
          )
        }

        {
          /* If successful and not reviewed, allow rejecting */
          isSuccessful(res[0]) && res[0].passedQa === null && (
            <RejectReportMenuItem
              handleClose={handleClose}
              report={res[0]}
              updateReportJobStatus={updateReportJobStatus}
              localData={localData}
            />
          )
        }

        {
          /* If successful and rejected, allow un-rejecting */
          isSuccessful(res[0]) && res[0].passedQa === false && (
            <UnRejectReportMenuItem
              handleClose={handleClose}
              report={res[0]}
              updateReportJobStatus={updateReportJobStatus}
              localData={localData}
            />
          )
        }

        {
          /* If approved , allow disappoving */
          res[0].passedQa && res[0].noOfProjectsAddedTo === 0 && (
            <DisapproveReportMenuItem
              handleClose={handleClose}
              report={res[0]}
              updateReportJobStatus={updateReportJobStatus}
              localData={localData}
            />
          )
        }

        {
          /* If successful and not an optimization report, allow running update products*/
          isSuccessful(res[0]) && res[0].reportType !== 'optimization' && (
            <UpdateReportMenuItem 
              handleClose={handleClose} 
              report={res[0]} 
              setShowUpdateReportDisabledModal={setShowUpdateReportDisabledModal}
            />
          )
        }

        {
          /* If successful and not reviewed, allow approving only market map */
          isSuccessful(res[0]) && res[0].passedQa === null && (
            <ApproveMapOnlyMenuItem
              handleClose={handleClose}
              report={res[0]}
              setShowMapOnlyApprovalModal={setShowMapOnlyApprovalModal}
              setReportId={setReportId}
              setReportRowId={setReportRowId}
            />
          )
        }

        {
          /* If has customClassDb , allow modal */
          hasCustomClassDbProducts(res[0]) && (
            <CustomClassDbReportMenuItem
              handleClose={handleClose}
              report={res[0]}
              setShowCustomClassDbModal={setShowCustomClassDbModal}
            />
          )
        }
      </Menu>

      {/* Modals */}
      <CustomClassDbModal
        show={showCustomClassDbModal}
        close={() => setShowCustomClassDbModal(false)}
        report={res[0]}
      />

      {/* Update Report Disabled Modal */}
      <Modal 
        open={showUpdateReportDisabledModal} 
        onClose={() => setShowUpdateReportDisabledModal(false)}
        className={classes.modal}
      >
        <div className={classes.modalContainer}>
          <h3 style={{ marginBottom: '1rem' }}>Update Report Access Temporarily Disabled</h3>
          <p style={{ marginBottom: '1.5rem', lineHeight: '1.5' }}>
            Update Product has a narrow use case, so access is temporarily disabled for review. 
            Please submit a JIRA ticket to request it.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <MaterialButton 
              variant="outlined" 
              soft 
              onClick={() => setShowUpdateReportDisabledModal(false)}
            >
              Close
            </MaterialButton>
            <MaterialButton 
              variant="outlined"
              soft
              teal
              onClick={() => {
                window.open(
                  'https://adlm.nielseniq.com/jira/servicedesk/customer/portal/52/create/1244',
                  '_blank',
                  'noopener,noreferrer'
                );
              }}
            >
              Submit JIRA Ticket
            </MaterialButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

ReportViewerMenu.displayName = 'ReportViewerMenu';

export default ReportViewerMenu;
